import {
  ChartColumnIncreasing,
  CloudSync,
  Code,
  Groceries,
  HeartPlus,
  Info,
  MessageCircleHeart,
  MonitorSmartphone,
  Sprout,
  Wheat,
  type LucideIcon,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";

import { cn } from "@/lib/utils";
import { getStoredSessionRedirectPath } from "../../utils/authentication";

const LIVE_MODE_URL = "https://farm2go.wfp.org/";

const leadingTrainingIcons = [Sprout, Wheat, Groceries, MessageCircleHeart];
const trailingTrainingIcons = [MonitorSmartphone, CloudSync, ChartColumnIncreasing, HeartPlus];

type Variant = "trainingLogin" | "training" | "other";

const variants: Record<Variant, { className: string; iconClassName: string }> = {
  trainingLogin: {
    className: "bg-warning-100 border-warning-200 text-warning-800",
    iconClassName: "text-warning-700",
  },
  training: {
    className: "bg-warning-50 border-warning-200 text-warning-800",
    iconClassName: "text-warning-700",
  },
  other: {
    className: "bg-info-50 border-info-200 text-blue-800",
    iconClassName: "text-blue-700",
  },
};

const IconGroup = ({ icons, className }: { icons: LucideIcon[]; className: string }) => (
  <div className="flex items-center gap-2 max-sm:hidden" aria-hidden="true">
    {icons.map((Icon, index) => (
      <Icon key={index} className={cn("size-5 shrink-0", className)} />
    ))}
  </div>
);

export const EnvironmentIndicator = () => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const environment = process.env.SENTRY_ENVIRONMENT;

  if (!environment || environment === "PROD") return null;

  const isLogin = pathname === "/login";
  const variant: Variant = environment !== "QA" ? "other" : isLogin ? "trainingLogin" : "training";
  if (variant === "other" && getStoredSessionRedirectPath()) return null;

  const { className, iconClassName } = variants[variant];

  return (
    <div role="status" className={cn("flex justify-center border-b px-4 py-3 text-sm", className)}>
      {variant === "trainingLogin" && (
        <div className="flex items-center gap-4">
          <Info aria-hidden="true" className={cn("size-7 shrink-0", iconClassName)} />
          <div className="flex flex-col gap-1 text-center">
            <p className="font-semibold">{t("environmentIndicator.trainingMode")}</p>
            <p>
              {t("environmentIndicator.loginDescription")}{" "}
              <a href={LIVE_MODE_URL} className="font-bold underline underline-offset-4">
                {t("environmentIndicator.liveMode")}
              </a>
              .
            </p>
          </div>
        </div>
      )}
      {variant === "training" && (
        <div className="flex items-center gap-3">
          <IconGroup icons={leadingTrainingIcons} className={iconClassName} />
          <span className="font-bold">{t("environmentIndicator.trainingMode")}</span>
          <IconGroup icons={trailingTrainingIcons} className={iconClassName} />
        </div>
      )}
      {variant === "other" && (
        <div className="flex items-center gap-3">
          <Code aria-hidden="true" className={cn("size-5 shrink-0", iconClassName)} />
          <span className="font-semibold">{t("environmentIndicator.devMode")}</span>
        </div>
      )}
    </div>
  );
};
